import React, { useState, useEffect } from "react";
import {
  FaCheckCircle,
  FaSpinner,
  FaTimes,
  FaUpload,
  FaYoutube,
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaEye,
  FaVideo,
  FaRedo,
  FaCreditCard,
  FaHistory,
} from "react-icons/fa";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import DescriptionViewer from "../../../Common/DescriptionViewer";
import ConferenceSubNav from "../../../Common/ConferenceSubNav";

const AcceptedPaidConference = () => {
  const [conferences, setConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedConference, setSelectedConference] = useState(null);
  const [isSubmittingProof, setIsSubmittingProof] = useState(false);
  const [formData, setFormData] = useState({
    conferenceId: "",
    screenshot: null,
    channelName: "",
    videoLink: "",
    platform: "",
    duration: "",
  });
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [availablePlatforms, setAvailablePlatforms] = useState([]);
  const navigate = useNavigate();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(conferences.length / itemsPerPage);

  const stats = {
    acceptedConferences: conferences.length,
    proofSubmitted: conferences.filter((conf) => conf.proofSubmitted).length,
    proofPending: conferences.filter((conf) => !conf.proofSubmitted).length,
  };

  const fetchAcceptedConferences = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/get/accepted/paid-conferences`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const conferencesData = response.data?.data || [];
      const sortedConferences = [...conferencesData].sort((a, b) => {
        const dateA = new Date(a.acceptedAt || 0).getTime();
        const dateB = new Date(b.acceptedAt || 0).getTime();
        return dateB - dateA;
      });

      setConferences(sortedConferences);
      setError(null);
    } catch (err) {
      console.error("Error fetching accepted conferences:", err);
      setError("Failed to load accepted conferences.");
      toast.error("Cloud sync failed");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlatforms = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/get/ad/pricing`);
      if (response.data.success) {
        setAvailablePlatforms(response.data.data.plateforms || []);
      }
    } catch (error) {
      console.error("Error fetching platforms:", error);
    }
  };

  useEffect(() => {
    fetchAcceptedConferences();
    fetchPlatforms();
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return [...conferences].slice(startIndex, endIndex);
  };

  const handleViewDetails = (conference) => {
    setSelectedConference(conference);
    setIsSubmittingProof(false);
    setShowModal(true);
  };

  const handleSubmitProofClick = (conferenceId) => {
    setFormData((prev) => ({
      ...prev,
      conferenceId: conferenceId,
    }));
    setIsSubmittingProof(true);
    setSelectedConference(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedConference(null);
    setIsSubmittingProof(false);
    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview);
      setScreenshotPreview(null);
    }
    setFormData({
      conferenceId: "",
      screenshot: null,
      channelName: "",
      videoLink: "",
      platform: "",
      duration: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "duration") {
      let cleanValue = value.replace(/[^\d:]/g, "");
      const prevValue = formData.duration;
      const isDeleting = value.length < prevValue.length;
      if (value === "") {
        setFormData((prev) => ({ ...prev, [name]: "" }));
        return;
      }
      if (isDeleting && cleanValue.includes(":") && cleanValue.length <= 3) {
        cleanValue = cleanValue.replace(":", "");
      }
      if (!isDeleting && cleanValue.length === 2 && !cleanValue.includes(":")) {
        cleanValue = cleanValue + ":";
      }
      if (cleanValue.replace(":", "").length === 0) cleanValue = "";
      if (cleanValue.length > 5) cleanValue = cleanValue.substring(0, 5);
      setFormData((prev) => ({ ...prev, [name]: cleanValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Limit: 5MB");
        return;
      }
      if (!file.type.startsWith("image/")) {
        toast.error("Images only");
        return;
      }
      setFormData((prev) => ({ ...prev, screenshot: file }));
      setScreenshotPreview(URL.createObjectURL(file));
    }
  };

  const removeScreenshot = () => {
    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview);
      setScreenshotPreview(null);
    }
    setFormData((prev) => ({ ...prev, screenshot: null }));
  };

  const handleSubmitProof = async (e) => {
    e.preventDefault();
    if (!formData.screenshot || !formData.channelName || !formData.videoLink || !formData.platform || !formData.duration || !formData.conferenceId) {
      toast.error("Fields required");
      return;
    }

    const youtubePattern = /^(https?:\/\/)?(www\.)?(youtu\.be\/|youtube\.com\/(watch\?|embed\/|v\/|shorts\/)?)/i;
    if (!youtubePattern.test(formData.videoLink.trim())) {
      toast.error("Invalid link");
      return;
    }

    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => submitData.append(key, formData[key]));
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/submit/paid-conference/proof/${formData.conferenceId}`,
        submitData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.status === 200) {
        toast.success("Uplink successful!");
        handleCloseModal();
        await fetchAcceptedConferences();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Uplink failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] pb-24 text-slate-900">
      {/* Premium Compact Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="px-3 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-600 text-[10px] font-black uppercase tracking-tight hover:bg-slate-100 transition-all whitespace-nowrap shadow-sm"
            >
              ← Go Back
            </button>
            <div className="w-1.5 h-8 bg-gradient-to-b from-indigo-500 to-blue-600 rounded-full" />
            <div>
              <h1 className="text-lg font-black text-slate-900 leading-tight">Project Hub</h1>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider leading-none">Active Paid Intel</span>
                <div className="w-1 h-1 rounded-full bg-slate-300" />
                <span className="text-[9px] font-black text-indigo-600 italic leading-none">{stats.acceptedConferences} Live Units</span>
              </div>
            </div>
          </div>
          <button
            onClick={fetchAcceptedConferences}
            disabled={loading}
            className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 active:rotate-180 transition-all duration-500"
          >
            <FaRedo className={`text-[10px] ${loading ? "animate-spin text-indigo-500" : ""}`} />
          </button>
        </div>
      </div>

      <ConferenceSubNav type="paid" />

      <div className="max-w-5xl mx-auto p-3 space-y-3">
        {/* Statistics Grid - Ultra Compact */}
        {!loading && !error && conferences.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-slate-900 rounded-2xl p-4 text-white relative overflow-hidden group">
              <div className="absolute -right-4 -bottom-4 w-12 h-12 bg-white/5 rounded-full blur-xl group-hover:bg-white/10 transition-all" />
              <p className="text-[7px] font-black text-slate-400 uppercase mb-1 tracking-widest leading-none">Total Units</p>
              <p className="text-sm font-black tracking-tighter tabular-nums text-white leading-none">{stats.acceptedConferences}</p>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-slate-100 relative group border-b-2 border-b-emerald-400">
              <p className="text-[7px] font-black text-slate-300 uppercase mb-1 tracking-widest leading-none">Reports Ready</p>
              <p className="text-sm font-black tracking-tighter tabular-nums text-emerald-600 leading-none">{stats.proofSubmitted}</p>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-slate-100 relative group border-b-2 border-b-amber-400">
              <p className="text-[7px] font-black text-slate-300 uppercase mb-1 tracking-widest leading-none">Scan Pending</p>
              <p className="text-sm font-black tracking-tighter tabular-nums text-amber-600 leading-none">{stats.proofPending}</p>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="relative">
              <div className="w-10 h-10 border-4 border-indigo-100 border-t-indigo-500 rounded-full animate-spin" />
              <FaVideo className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-indigo-500 text-[8px]" />
            </div>
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Pinging Uplinks...</p>
          </div>
        )}

        {/* Desktop View: Table (Ultra-Compact) */}
        {!loading && !error && conferences.length > 0 && (
          <div className="hidden lg:block bg-white rounded-[1.5rem] shadow-sm border border-slate-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50/50 text-slate-400 border-b border-slate-50">
                <tr>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Objective</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Yield</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Network Status</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Aquired At</th>
                  <th className="px-5 py-4 text-center text-[9px] font-black uppercase tracking-widest">Operations</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {getCurrentPageData().map((conference) => (
                  <tr key={conference._id} className="hover:bg-slate-50/30 transition-colors">
                    <td className="px-5 py-3 text-[10px] font-black text-slate-700 italic truncate max-w-[220px] leading-tight flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-indigo-50 flex items-center justify-center text-indigo-500 text-[8px]"><FaVideo /></div>
                      <span className="truncate">{conference.conferenceDetails?.topic || "Classified Subject"}</span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-emerald-50 border border-emerald-100/50 w-fit">
                        <FaCreditCard className="text-emerald-500 text-[8px]" />
                        <span className="text-[10px] font-black text-emerald-700 tabular-nums leading-none">₹{conference.commissionDetails?.amountPerReporter || 0}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest border ${conference.proofStatus === "approved" ? "bg-emerald-50 text-emerald-600 border-emerald-100" :
                        conference.proofStatus === "rejected" ? "bg-rose-50 text-rose-600 border-rose-100" :
                          conference.proofSubmitted ? "bg-blue-50 text-blue-600 border-blue-100" :
                            "bg-amber-50 text-amber-600 border-amber-100"
                        }`}>
                        {conference.proofStatus || (conference.proofSubmitted ? "Locked" : "Aquired")}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-[9px] text-slate-400 font-bold italic tabular-nums leading-none uppercase">
                      {conference.acceptedAt ? new Date(conference.acceptedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "---"}
                    </td>
                    <td className="px-5 py-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <button onClick={() => handleViewDetails(conference)} className="h-7 px-3 bg-slate-900 border border-slate-800 text-white rounded-lg text-[8px] font-black uppercase tracking-widest active:scale-95 transition-all flex items-center gap-1.5 shadow-sm"><FaEye /> Intel</button>
                        {(!conference.proofSubmitted || conference.canResubmit) && (
                          <button onClick={() => handleSubmitProofClick(conference.conferenceId)} className="h-7 px-3 bg-indigo-600 text-white rounded-lg text-[8px] font-black uppercase tracking-widest active:scale-95 transition-all flex items-center gap-1.5 shadow-lg shadow-indigo-100"><FaUpload /> Report</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Mobile View: High-Density Widgets */}
        {!loading && !error && (
          <div className="lg:hidden max-w-md mx-auto space-y-3">
            {conferences.length > 0 ? (
              getCurrentPageData().map((conference) => (
                <div key={conference._id} className="relative bg-white rounded-[1.75rem] border border-slate-100 p-1 shadow-sm active:scale-[0.99] transition-all overflow-hidden border-b-2 border-b-indigo-400/20">
                  <div className="bg-slate-50/40 rounded-[1.6rem] p-3.5 flex flex-col gap-3">
                    {/* Header */}
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-indigo-500 to-blue-600 p-0.5 shadow-md shadow-indigo-100/50 flex-shrink-0">
                        <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                          <FaVideo className="text-indigo-500 text-sm" />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-[12px] font-black text-slate-900 leading-tight truncate pr-2 uppercase italic">{conference.conferenceDetails?.topic || "N/A"}</h3>
                        <div className="flex items-center justify-between mt-0.5">
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest tabular-nums italic">Code #{conference.conferenceId}</p>
                          <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest shadow-sm ${conference.proofStatus === "approved" ? "bg-emerald-500 text-white" :
                            conference.proofStatus === "rejected" ? "bg-rose-500 text-white" :
                              conference.proofSubmitted ? "bg-blue-500 text-white" :
                                "bg-amber-500 text-white"
                            }`}>
                            {conference.proofStatus || (conference.proofSubmitted ? "Locked" : "Active")}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Quick Infos - Grid */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-white rounded-xl p-2.5 border border-slate-100/50 flex flex-col gap-1">
                        <p className="text-[7px] font-black text-slate-300 uppercase leading-none">Net Yield</p>
                        <p className="text-[11px] font-black text-emerald-600 tabular-nums italic tracking-tighter">₹{conference.commissionDetails?.amountPerReporter || 0}</p>
                      </div>
                      <div className="bg-white rounded-xl p-2.5 border border-slate-100/50 flex flex-col gap-1">
                        <p className="text-[7px] font-black text-slate-300 uppercase leading-none">Sector Deadline</p>
                        <p className="text-[10px] font-black text-slate-700 italic truncate tabular-nums leading-none mt-0.5">{conference.conferenceDetails?.conferenceDate}</p>
                      </div>
                    </div>

                    {/* Actions Group */}
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleViewDetails(conference)} className="flex-1 h-11 rounded-xl bg-slate-900 text-white shadow-lg shadow-slate-200/50 flex items-center justify-center gap-2 active:scale-95 transition-all text-[10px] font-black uppercase tracking-widest"><FaEye /> Intel</button>
                      {(!conference.proofSubmitted || conference.canResubmit) && (
                        <button onClick={() => handleSubmitProofClick(conference.conferenceId)} className="flex-1 h-11 rounded-xl bg-indigo-600 text-white shadow-lg shadow-indigo-100/50 flex items-center justify-center gap-2 active:scale-95 transition-all text-[10px] font-black uppercase tracking-widest"><FaUpload /> Report</button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-24 px-10 text-center">
                <div className="w-16 h-16 rounded-[2rem] bg-white shadow-lg flex items-center justify-center mb-6">
                  <FaHistory className="text-slate-100 text-3xl" />
                </div>
                <h2 className="text-base font-black text-slate-900 tracking-tight">Vault Empty</h2>
                <p className="text-[9px] font-bold text-slate-400 mt-2 px-6 uppercase tracking-wider italic leading-relaxed">No acquired paid assignments found in current sector logs.</p>
              </div>
            )}
          </div>
        )}

        {/* Compact Pagination Bar */}
        {!loading && !error && totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 py-4">
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="w-9 h-9 rounded-xl bg-white border border-slate-100 flex items-center justify-center text-slate-400 disabled:opacity-20 shadow-sm transition-all"><FaRedo className="-rotate-90 text-[9px]" /></button>
            <div className="h-9 px-4 rounded-xl bg-slate-900 flex items-center gap-2 shadow-xl shadow-slate-100">
              <span className="text-[10px] font-black text-white italic">{currentPage}</span>
              <div className="w-1 h-1 rounded-full bg-slate-700" />
              <span className="text-[10px] font-black text-slate-500 italic">{totalPages}</span>
            </div>
            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="w-9 h-9 rounded-xl bg-white border border-slate-100 flex items-center justify-center text-slate-400 disabled:opacity-20 shadow-sm transition-all"><FaRedo className="rotate-90 text-[9px]" /></button>
          </div>
        )}
      </div>

      {/* Official Case Modal */}
      {showModal && selectedConference && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={handleCloseModal} />
          <div className="relative w-full max-w-lg bg-white rounded-[2.5rem] overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="bg-gradient-to-r from-slate-900 to-indigo-950 p-6 text-white relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/5 rounded-full blur-3xl" />
              <div className="relative z-10 flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-black tracking-tighter leading-tight mb-1">Unit Assignment Log</h3>
                  <p className="text-[9px] font-black uppercase tracking-[0.2em] text-indigo-400 italic">Project technical specifications</p>
                </div>
                <button onClick={handleCloseModal} className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-all"><FaTimes className="text-xs" /></button>
              </div>
            </div>

            <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto custom-scrollbar">
              <div className="bg-indigo-50/50 rounded-2xl p-4 border border-indigo-100 flex items-center justify-between">
                <div className="flex items-center gap-3 text-indigo-900">
                  <FaVideo className="text-[10px]" />
                  <span className="text-[10px] font-black uppercase tracking-widest leading-none">Status: {selectedConference.proofStatus || "Unit Active"}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                  <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-2">Subject Target</p>
                  <p className="text-[11px] font-black text-slate-800 leading-tight italic truncate uppercase">{selectedConference.conferenceDetails?.topic || "N/A"}</p>
                </div>
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                  <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-2">Net Revenue</p>
                  <p className="text-[12px] font-black text-emerald-600 tabular-nums italic leading-none">₹{selectedConference.commissionDetails?.amountPerReporter || 0}</p>
                </div>
              </div>

              <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100 space-y-3">
                <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest">Tactical Location</p>
                <div className="flex items-center gap-3 text-[10px] font-black text-slate-800">
                  <FaMapMarkerAlt className="text-indigo-500" />
                  <span className="italic uppercase truncate">{selectedConference.conferenceDetails?.city}, {selectedConference.conferenceDetails?.state}</span>
                </div>
                <p className="text-[9px] font-bold text-slate-500 uppercase leading-relaxed italic truncate">{selectedConference.conferenceDetails?.place} ({selectedConference.conferenceDetails?.landmark})</p>
              </div>

              <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-2">Mission Intelligence</p>
                <DescriptionViewer description={selectedConference.conferenceDetails?.purpose || "No intel brief provided."} wordLimit={20} textSize="text-[10px]" buttonText="Read Full Brief" modalTitle="Mission Intel" />
              </div>
            </div>

            <div className="p-6 bg-slate-50 flex items-center justify-center px-8 border-t border-slate-100/50">
              <button onClick={handleCloseModal} className="h-10 w-full bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-[0.3em] shadow-lg shadow-slate-200 active:scale-95 transition-all">Close Log</button>
            </div>
          </div>
        </div>
      )}

      {/* Proof Submission Modal */}
      {showModal && isSubmittingProof && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-xl" onClick={handleCloseModal} />
          <div className="relative w-full max-w-lg bg-white rounded-[2.5rem] overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300 border border-slate-100">
            <div className="bg-slate-900 p-7 text-white relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-3xl" />
              <h3 className="text-xl font-black tracking-tighter leading-tight mb-1 relative z-10">Uplink Proof Transmission</h3>
              <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500 italic relative z-10">Secure cryptographic evidence submission</p>
              <button onClick={handleCloseModal} className="absolute top-6 right-6 w-8 h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-all"><FaTimes className="text-xs" /></button>
            </div>

            <form onSubmit={handleSubmitProof} className="p-7 space-y-5 max-h-[75vh] overflow-y-auto custom-scrollbar">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Protocol (Platform)</p>
                  <select name="platform" value={formData.platform} onChange={handleInputChange} className="w-full h-11 px-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-[11px] font-bold text-slate-700 focus:outline-none focus:border-indigo-100 transition-all appearance-none cursor-pointer">
                    <option value="">Select Platform</option>
                    {availablePlatforms.length > 0 ? (
                      availablePlatforms.map((platform, idx) => (
                        <option key={idx} value={platform}>
                          {platform}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="Youtube">Youtube Mainframe</option>
                        <option value="Facebook">Meta Network</option>
                        <option value="Instagram">Social Grid</option>
                        <option value="Twitter">X Intelligence</option>
                        <option value="Other">External Link</option>
                      </>
                    )}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Channel Signature</p>
                  <input type="text" name="channelName" value={formData.channelName} onChange={handleInputChange} placeholder="Channel Name" className="w-full h-11 px-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-[11px] font-bold text-slate-700 focus:outline-none focus:border-indigo-100 transition-all" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Transmission Duration</p>
                  <input type="text" name="duration" value={formData.duration} onChange={handleInputChange} placeholder="MM:SS" className="w-full h-11 px-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-[11px] font-bold text-slate-700 tabular-nums focus:outline-none focus:border-indigo-100 transition-all" />
                </div>
                <div className="space-y-1.5">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Unit ID</p>
                  <input type="text" value={formData.conferenceId} disabled className="w-full h-11 px-4 bg-slate-100/50 border-2 border-transparent rounded-2xl text-[11px] font-black text-slate-400 tabular-nums" />
                </div>
              </div>

              <div className="space-y-1.5">
                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Uplink URL (Youtube Link)</p>
                <div className="relative">
                  <input type="url" name="videoLink" value={formData.videoLink} onChange={handleInputChange} placeholder="https://youtube.com/watch?v=..." className="w-full h-11 px-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-[11px] font-bold text-slate-700 pr-10 focus:outline-none focus:border-indigo-100 transition-all" />
                  <FaYoutube className="absolute right-4 top-1/2 -translate-y-1/2 text-rose-500 text-sm" />
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Visual Evidence (Screenshot)</p>
                <div className={`relative w-full border-2 border-dashed rounded-[2rem] transition-all flex flex-col items-center justify-center p-6 ${screenshotPreview ? 'border-indigo-200 bg-indigo-50/20' : 'border-slate-100 bg-slate-50 hover:bg-slate-100/50 hover:border-indigo-100 cursor-pointer'}`}>
                  {screenshotPreview ? (
                    <div className="relative w-full">
                      <img src={screenshotPreview} alt="Screenshot Evidence" className="w-full h-40 object-cover rounded-2xl shadow-lg border border-white" />
                      <button type="button" onClick={removeScreenshot} className="absolute -top-2 -right-2 w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center shadow-lg active:scale-90 transition-all"><FaTimes className="text-xs" /></button>
                    </div>
                  ) : (
                    <>
                      <input type="file" name="screenshot" accept="image/*" onChange={handleFileChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20" />
                      <div className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center mb-3">
                        <FaUpload className="text-indigo-500 text-xs animate-bounce" />
                      </div>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter text-center">Drag visual evidence here or tap to <span className="text-indigo-600 underline">Upload</span></p>
                    </>
                  )}
                </div>
              </div>

              <div className="pt-2 flex items-center gap-3">
                <button type="submit" className="flex-[2] h-12 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-[0.3em] shadow-lg shadow-slate-200 active:scale-95 transition-all">Initiate Uplink</button>
                <button type="button" onClick={handleCloseModal} className="flex-1 h-12 bg-white border border-slate-200 text-slate-400 rounded-xl text-[10px] font-black uppercase tracking-widest active:bg-slate-50 transition-all">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ToastContainer position="top-center" theme="dark" hideProgressBar />
    </div>
  );
};

export default AcceptedPaidConference;
